import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import API_BASE_URL from '../config/api';
import { TONES, formatCurrency, formatDate, getBalance, getPaidAmount, todayInputValue } from './financeUtils';

const inputStyle = {
  width: '100%',
  padding: spacing.md,
  border: '2px solid ' + colors.border,
  borderRadius: '0.5rem',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontWeight: 700,
  fontSize: '0.85rem',
  color: colors.text.primary,
  marginBottom: spacing.xs,
};

const chipStyle = {
  padding: spacing.xs + ' ' + spacing.sm,
  borderRadius: '0.375rem',
  border: '1px solid ' + colors.border,
  backgroundColor: 'white',
  color: colors.text.primary,
  fontWeight: 700,
  fontSize: '0.75rem',
  cursor: 'pointer',
};

// Lets the admin record a part payment (instalment) on an invoice: enter how
// much has been paid now and the remaining balance is recalculated.
function FinancePaymentModal({ config, invoice, onClose, onSaved }) {
  const balance = getBalance(invoice);
  const alreadyPaid = getPaidAmount(invoice);
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayInputValue());
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAmount(balance > 0 ? String(balance) : '');
    setPaymentDate(todayInputValue());
    setNote('');
    setError('');
  }, [invoice, balance]);

  const entered = parseFloat(String(amount).replace(/,/g, ''));
  const validEntered = Number.isFinite(entered) && entered > 0 ? entered : 0;
  const remainingAfter = Math.max(Math.round((balance - validEntered) * 100) / 100, 0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!Number.isFinite(entered) || entered <= 0) {
      setError('Enter the amount that has been paid.');
      return;
    }
    if (entered > balance) {
      setError('The payment cannot be more than the remaining balance of ' + formatCurrency(balance) + '.');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        API_BASE_URL + '/api/admin/finance/' + config.endpoint + '/' + invoice.id + '/payment',
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token'),
          },
          body: JSON.stringify({ amount: entered, paymentDate, note }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.message || 'Could not record the payment.');
        setSaving(false);
        return;
      }

      const updatedBalance = data.data ? getBalance(data.data) : remainingAfter;
      onSaved(
        updatedBalance > 0
          ? 'Payment recorded. Remaining balance ' + formatCurrency(updatedBalance)
          : 'Payment recorded. Invoice fully paid',
        data.data
      );
    } catch (err) {
      setError('Connection error: ' + err.message);
      setSaving(false);
    }
  };

  const summaryRow = (label, value, tone) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.md, padding: spacing.sm + ' 0' }}>
      <span style={{ color: colors.text.secondary, fontSize: '0.85rem' }}>{label}</span>
      <span style={{ fontWeight: 800, color: tone ? TONES[tone].color : colors.text.primary, fontSize: '0.9rem' }}>{value}</span>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 70,
        padding: spacing.md,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        onClick={(event) => event.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows.xl,
          width: '100%',
          maxWidth: '520px',
          maxHeight: '92vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            padding: spacing.lg,
            borderBottom: '2px solid ' + colors.border,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: spacing.md,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: colors.text.primary }}>Record Payment</h3>
            <p style={{ margin: spacing.xs + ' 0 0', fontSize: '0.85rem', color: colors.text.secondary }}>
              {invoice.invoiceNumber} &middot; {invoice.partyName}
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            style={{ border: 'none', background: 'transparent', fontSize: '1.4rem', cursor: 'pointer', color: colors.text.secondary }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: spacing.lg }}>
          <div style={{ backgroundColor: colors.surface, borderRadius: '0.75rem', padding: spacing.md, marginBottom: spacing.lg }}>
            {summaryRow(config.amountLabel, formatCurrency(invoice.amount))}
            {summaryRow('Already paid', formatCurrency(alreadyPaid), alreadyPaid > 0 ? 'paid' : undefined)}
            {summaryRow('Balance to pay', formatCurrency(balance), balance > 0 ? 'unpaid' : 'paid')}
            {invoice.dueDate ? summaryRow('Due date', formatDate(invoice.dueDate)) : null}
          </div>

          <div>
            <label style={labelStyle} htmlFor='pd-payment-amount'>
              Amount paid now *
            </label>
            <input
              id='pd-payment-amount'
              type='number'
              min='0'
              step='0.01'
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
                setError('');
              }}
              style={inputStyle}
              autoFocus
            />
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginTop: spacing.sm }}>
              <button
                type='button'
                onClick={() => setAmount(String(balance))}
                style={{ ...chipStyle, borderColor: TONES.paid.border, backgroundColor: TONES.paid.background, color: TONES.paid.color }}
              >
                Full balance ({formatCurrency(balance)})
              </button>
              <button type='button' onClick={() => setAmount(String(Math.round(balance / 2)))} style={chipStyle}>
                Half ({formatCurrency(Math.round(balance / 2))})
              </button>
            </div>
          </div>

          <div className='pd-form-grid' style={{ marginTop: spacing.md }}>
            <div>
              <label style={labelStyle} htmlFor='pd-payment-date'>
                Payment date
              </label>
              <input
                id='pd-payment-date'
                type='date'
                value={paymentDate}
                onChange={(event) => setPaymentDate(event.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor='pd-payment-note'>
                Note (optional)
              </label>
              <input
                id='pd-payment-note'
                type='text'
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder='e.g. NEFT / cash / cheque no.'
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              marginTop: spacing.md,
              padding: spacing.md,
              borderRadius: '0.5rem',
              border: '1px solid ' + TONES.partial.border,
              backgroundColor: TONES.partial.background,
              display: 'flex',
              justifyContent: 'space-between',
              gap: spacing.md,
            }}
          >
            <span style={{ color: TONES.partial.color, fontSize: '0.85rem', fontWeight: 700 }}>Balance after this payment</span>
            <span style={{ color: TONES.partial.color, fontSize: '0.95rem', fontWeight: 900 }}>{formatCurrency(remainingAfter)}</span>
          </div>

          {error ? (
            <div
              style={{
                marginTop: spacing.md,
                padding: spacing.md,
                borderRadius: '0.5rem',
                backgroundColor: '#fdeaea',
                color: '#b42318',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.md, marginTop: spacing.lg, flexWrap: 'wrap' }}>
            <button
              type='button'
              onClick={onClose}
              disabled={saving}
              style={{
                padding: spacing.md + ' ' + spacing.lg,
                borderRadius: '0.5rem',
                border: '2px solid ' + colors.border,
                backgroundColor: 'white',
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                color: colors.text.primary,
              }}
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={saving}
              style={{
                padding: spacing.md + ' ' + spacing.lg,
                borderRadius: '0.5rem',
                border: 'none',
                backgroundColor: saving ? colors.neutral[400] : colors.primary[500],
                color: 'white',
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save Payment'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default FinancePaymentModal;



